import * as angular from 'angular';
import {IController} from 'angular';
import {AccountModel} from "../../angular/models/account.model";
import {AccountRequests} from "../../services/account-requests.service";
import {AccountPreferredAgent} from "../../models/account-preferred-agent.model";
import {SplitService} from "../../../commons/services/split-service.service";
import {AgentRankingDialogService} from "../../services/agent-ranking-dialog.service";
import {AccountsService} from "../../services/accounts-service.service";
import * as _ from "lodash";

export class AgentRankingViewCtrl implements IController {

  account: AccountModel;


  public agents: AccountPreferredAgent[] = [];
  public isLoading: boolean = false;
  public errorMessage: string = '';
  public isScrollable: boolean = false;

  // Pagination properties
  public paginationData: any = null;
  public page: number = 0;
  public size: number = 50;
  public paginationPages: number[] = [];

  // Selection properties
  selectedKeys: { [key: string]: boolean } = {};

  /* @ngInject */
  constructor(
    private $mdMedia,
    private accountRequests: AccountRequests,
    private splitService: SplitService,
    private agentRankingDialogService: AgentRankingDialogService,
    private accountsService: AccountsService
  ) {

  }

  $onInit() {
    this.selectedKeys = {};

    if (this.account?.id) {
      this.loadPreferredAgents();
    }
  }

  $doCheck() {
    this.isScrollable = !this.$mdMedia('gt-sm');
  };

  private loadPreferredAgents() {
    this.isLoading = true;
    this.errorMessage = '';

    const siteId = this.splitService.getSiteId();

    this.accountRequests.getPreferredAgents(this.account.id, {
      siteId: siteId,
      page: this.page,
      size: this.size,
      sort: 'priority,desc' // Sort by priority descending to show highest priority first
    }).then((response: any) => {
      // Handle paginated response
      if (response && response.content) {
        this.agents = _.sortBy(response.content, 'priority');
        this.paginationData = response;
        // this.updatePaginationPages();
      } else if (Array.isArray(response)) {
        // Handle direct array response if API returns simple array
        this.agents = response;
        this.paginationData = null;
      } else {
        this.agents = [];
        this.paginationData = null;
      }

      this.isLoading = false;
      this.updateSelectionState();
    }).catch((error) => {
      this.errorMessage = 'Failed to load agent rankings. Please try again.';
      this.agents = [];
      this.paginationData = null;
      this.isLoading = false;
      this.updateSelectionState();
    });
  }


  // private updatePaginationPages() {
  //   if (!this.paginationData) return;
  //
  //   const totalPages = this.paginationData.totalPages || 1;
  //   const currentPage = this.paginationData.number || 0;
  //
  //   this.paginationPages = [];
  //
  //   // Always show at least the current page
  //   if (totalPages === 1) {
  //     this.paginationPages = [0]; // Show page 1 (index 0)
  //     return;
  //   }
  //
  //   let startPage = Math.max(0, currentPage - 2);
  //   let endPage = Math.min(totalPages - 1, currentPage + 2);
  //
  //   // Adjust start page if we don't have enough pages at the end
  //   if (endPage - startPage < 4 && startPage > 0) {
  //     startPage = Math.max(0, endPage - 4);
  //   }
  //
  //   // Adjust end page if we don't have enough pages at the start
  //   if (endPage - startPage < 4 && endPage < totalPages - 1) {
  //     endPage = Math.min(totalPages - 1, startPage + 4);
  //   }
  //
  //   for (let i = startPage; i <= endPage; i++) {
  //     this.paginationPages.push(i);
  //   }
  // }

  onPaginationChange(page: number) {
    this.page = page;
    this.loadPreferredAgents();
  }

  onPaginationBrowse(isNext: boolean) {
    if (isNext && !this.paginationData?.last) {
      this.page++;
      this.loadPreferredAgents();
    } else if (!isNext && !this.paginationData?.first) {
      this.page--;
      this.loadPreferredAgents();
    }
  }

  onPageSizeChange(newSize: number) {
    this.size = newSize;
    this.page = 0; // Reset to first page when changing size
    this.loadPreferredAgents();
  }

  refreshAgents() {
    this.loadPreferredAgents();
  }

  onAddAgent(event: Event) {
    this.agentRankingDialogService.showAddAgentDialog(
      event,
      this.account.id,
      this.agents
    ).then((payload) => {
      this._addPreferredAgent(payload);
    }, () => {
      // Dialog cancelled
    });
  }

  onEditAgent(agent: any, event: Event) {
    this.agentRankingDialogService.showEditAgentDialog(
      event,
      agent
    ).then((payload) => {
      this._updatedPreferredAgent(payload)
    }, () => {
      // Dialog cancelled
    });
  }


  // Selection management methods

  getSelectedAgentIds(): string[] {
    return Object.keys(this.selectedKeys).filter(id => this.selectedKeys[id]);
  }

  getSelectedAgentsCount(): number {
    return this.getSelectedAgentIds().length;
  }

  private updateSelectionState() {
    // Clear selection when data changes (e.g., pagination, refresh)
    this.selectedKeys = {};
  }

  onBatchDelete(event: Event) {
    const selectedIds = this.getSelectedAgentIds();
    if (selectedIds.length === 0) {
      return;
    }

    this.agentRankingDialogService.showBatchDeleteConfirmDialog(event, selectedIds.length, 'PREFERRED').then(() => {
      this.performBatchDelete(selectedIds);
    }, () => {
      // Dialog cancelled
    });
  }

  private performBatchDelete(agentIds: string[]) {
    this.isLoading = true;
    const siteId = this.splitService.getSiteId();

    this.accountsService.deletePreferredAgents(this.account.id, agentIds)
      .then((result: any) => {
        this.refreshAgents(); // Refresh the table
        this.updateSelectionState(); // Clear selection
      })
      .catch((error) => {
        this.errorMessage = 'Failed to delete selected agents. Please try again.';
        this.isLoading = false;
      });
  }

  private _addPreferredAgent(agentData) {
    this.accountsService.createPreferredAgent(this.account.id, agentData)
      .then(() => {
        this.refreshAgents();
      })
  }


  private _updatedPreferredAgent(updatedAgent) {
    this.accountsService.updatePreferredAgents(this.account.id, [updatedAgent])
      .then((response: any) => {
        this.refreshAgents();
      })

  }
}

angular
  .module('bringoz.admin.accounts')
  .component('agentRankingView', {
    bindings: {
      account: '<'
    },
    template: require('./agent-ranking-view.html').default,
    controller: AgentRankingViewCtrl
  });
